import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  AiAssistantChat,
  AiAssistantDocumentContext,
  AiAssistantMessage,
  AiAssistantPageContext,
} from '@asset-metrix/portal-ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  deleteAiChatSession,
  fetchAiChatSessionHistory,
  fetchAiChatSessions,
  streamAiAssistantChat,
} from '../../api/portalGateway/aiAssistantChat';
import type { DocumentChatEvent } from '../../api/portalGateway/documentChat';

import {
  mapCitationSources,
  mapStoredCitations,
  toInlineCitationTokens,
} from './citations';
import { buildPrompt, splitUserContent } from './pageContext';

const AI_CHAT_SESSIONS_QUERY_KEY = ['aiChatSessions'] as const;
function isDraftChatId(chatId: string): boolean {
  return chatId.startsWith('new-chat-');
}

function createMessageId(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

function mapHistoryMessages(
  sessionId: string,
  messages: Awaited<ReturnType<typeof fetchAiChatSessionHistory>>
): AiAssistantMessage[] {
  return messages.map((message, index) => {
    const sources = mapStoredCitations(message.citations);
    const isAssistant = message.role === 'agent';
    if (!isAssistant) {
      const { text, pageContexts, documentContexts } = splitUserContent(
        message.content
      );
      return {
        id: `${sessionId}-${message.timestamp}-${index}`,
        sender: 'user' as const,
        text,
        ...(pageContexts ? { pageContexts } : {}),
        ...(documentContexts ? { documentContexts } : {}),
      };
    }
    return {
      id: `${sessionId}-${message.timestamp}-${index}`,
      sender: 'assistant' as const,
      text:
        sources.length > 0
          ? toInlineCitationTokens(message.content, sources)
          : message.content,
      ...(sources.length > 0 ? { sources } : {}),
    };
  });
}

type LocalConversation = {
  chatId: string;
  messages: AiAssistantMessage[];
};

function updateLastIncompleteAssistant(
  current: LocalConversation | null,
  update: (reply: AiAssistantMessage) => AiAssistantMessage
): LocalConversation | null {
  if (!current) {
    return current;
  }
  const reply = current.messages.at(-1);
  if (reply?.sender !== 'assistant' || reply.complete !== false) {
    return current;
  }
  return {
    ...current,
    messages: [...current.messages.slice(0, -1), update(reply)],
  };
}

function isSameStatus(
  status: NonNullable<AiAssistantMessage['statuses']>[number],
  event: { code?: string; content: string }
): boolean {
  if (event.code && status.code) {
    return status.code === event.code;
  }
  return status.content.trim() === event.content.trim();
}

function appendCompletedStatus(
  reply: AiAssistantMessage,
  event: { code?: string; content: string }
): AiAssistantMessage {
  const completedStatuses = [
    ...(reply.completedStatuses ?? []),
    { code: event.code, content: event.content },
  ];
  const matchIndex = (reply.statuses ?? []).findIndex((status) =>
    isSameStatus(status, event)
  );
  if (matchIndex === -1) {
    return { ...reply, completedStatuses };
  }

  return {
    ...reply,
    completedStatuses,
    statuses: reply.statuses?.filter((_, index) => index !== matchIndex),
  };
}

export function useChat(newChatTitle: string) {
  const queryClient = useQueryClient();
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [draftChat, setDraftChat] = useState<AiAssistantChat | null>(null);
  const [localConversation, setLocalConversation] =
    useState<LocalConversation | null>(null);
  // Send time for a server chat, so the rail moves before the sessions refetch.
  const [recentActivityAt, setRecentActivityAt] = useState<
    Record<string, number>
  >({});
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const streamRef = useRef<{ abort: () => void } | null>(null);
  const localConversationRef = useRef<LocalConversation | null>(null);

  const sessionsQuery = useQuery({
    queryKey: AI_CHAT_SESSIONS_QUERY_KEY,
    queryFn: fetchAiChatSessions,
  });

  const activeIsDraft = activeChatId !== null && isDraftChatId(activeChatId);
  const historyQuery = useQuery({
    queryKey: ['aiChatSessionHistory', activeChatId],
    queryFn: () => fetchAiChatSessionHistory(activeChatId as string),
    enabled: activeChatId !== null && !activeIsDraft,
  });

  useEffect(() => {
    localConversationRef.current = localConversation;
  }, [localConversation]);

  useEffect(() => {
    if (
      activeChatId === null &&
      draftChat === null &&
      sessionsQuery.data?.length
    ) {
      setActiveChatId(sessionsQuery.data[0].sessionId);
    }
  }, [activeChatId, draftChat, sessionsQuery.data]);

  useEffect(
    () => () => {
      streamRef.current?.abort();
    },
    []
  );

  const chats = useMemo<AiAssistantChat[]>(() => {
    const serverChats = (sessionsQuery.data ?? []).map((session) => {
      const serverMessages =
        activeChatId === session.sessionId
          ? mapHistoryMessages(session.sessionId, historyQuery.data ?? [])
          : [];
      const messages =
        localConversation?.chatId === session.sessionId
          ? localConversation.messages
          : serverMessages;

      return {
        id: session.sessionId,
        title: session.preview || newChatTitle,
        createdAt: new Date(session.createdAt).getTime(),
        updatedAt: Math.max(
          new Date(session.updatedAt).getTime(),
          recentActivityAt[session.sessionId] ?? 0
        ),
        messages,
      };
    });

    if (!draftChat) {
      return serverChats;
    }

    const draftAlreadyListed =
      !isDraftChatId(draftChat.id) &&
      (sessionsQuery.data ?? []).some(
        (session) => session.sessionId === draftChat.id
      );
    if (draftAlreadyListed) {
      return serverChats;
    }

    const messages =
      localConversation?.chatId === draftChat.id
        ? localConversation.messages
        : draftChat.messages;
    return [{ ...draftChat, messages }, ...serverChats];
  }, [
    activeChatId,
    draftChat,
    historyQuery.data,
    localConversation,
    newChatTitle,
    recentActivityAt,
    sessionsQuery.data,
  ]);

  const finishLastAssistantReply = useCallback(() => {
    setLocalConversation((current) => {
      if (!current) {
        return current;
      }
      const reply = current.messages.at(-1);
      if (reply?.sender !== 'assistant' || reply.complete !== false) {
        return current;
      }
      return {
        ...current,
        messages: [
          ...current.messages.slice(0, -1),
          {
            ...reply,
            complete: true,
            statuses: undefined,
            completedStatuses: undefined,
            statusBoundaryPending: undefined,
          },
        ],
      };
    });
  }, []);

  const abortStream = useCallback(() => {
    streamRef.current?.abort();
    streamRef.current = null;
    setIsStreaming(false);
    finishLastAssistantReply();
  }, [finishLastAssistantReply]);
  const clearError = useCallback(() => setError(null), []);

  const selectChat = useCallback(
    (chatId: string) => {
      if (chatId !== activeChatId) {
        abortStream();
        const conversation = localConversationRef.current;
        if (conversation) {
          setDraftChat((draft) => {
            if (draft && draft.id === conversation.chatId) {
              return { ...draft, messages: conversation.messages };
            }
            return draft;
          });
        }
        setLocalConversation(null);
      }
      setActiveChatId(chatId);
      setError(null);
    },
    [abortStream, activeChatId]
  );

  const createChat = useCallback(() => {
    abortStream();
    const now = Date.now();
    const chat: AiAssistantChat = {
      id: `new-chat-${now}`,
      title: newChatTitle,
      messages: [],
      createdAt: now,
      updatedAt: now,
    };
    setDraftChat(chat);
    setLocalConversation(null);
    setActiveChatId(chat.id);
    setError(null);
  }, [abortStream, newChatTitle]);

  const promoteDraft = useCallback((sessionId: string) => {
    const conversation = localConversationRef.current;
    setDraftChat((current) => {
      if (!current) {
        return current;
      }
      const shouldSyncMessages =
        conversation != null &&
        (conversation.chatId === current.id ||
          isDraftChatId(conversation.chatId));
      return {
        ...current,
        id: sessionId,
        messages: shouldSyncMessages ? conversation.messages : current.messages,
      };
    });
    setLocalConversation((current) =>
      current ? { ...current, chatId: sessionId } : current
    );
    setActiveChatId((current) =>
      current !== null && isDraftChatId(current) ? sessionId : current
    );
  }, []);

  const handleStreamEvent = useCallback(
    (event: DocumentChatEvent) => {
      if (event.type === 'session') {
        promoteDraft(event.session_id);
        return;
      }

      if (event.type === 'done') {
        finishLastAssistantReply();
        promoteDraft(event.session_id);
        return;
      }

      if (event.type === 'error') {
        setError(new Error(event.message));
        setIsStreaming(false);
        finishLastAssistantReply();
        return;
      }

      if (event.type === 'status') {
        setLocalConversation((current) =>
          updateLastIncompleteAssistant(current, (reply) => ({
            ...reply,
            statuses: [
              ...(reply.statuses ?? []),
              { code: event.code, content: event.content },
            ],
            statusBoundaryPending: reply.text !== '',
          }))
        );
        return;
      }

      if (event.type === 'status_done') {
        setLocalConversation((current) =>
          updateLastIncompleteAssistant(current, (reply) =>
            appendCompletedStatus(reply, event)
          )
        );
        return;
      }

      if (event.type === 'citations') {
        const sources = mapCitationSources(event);
        setLocalConversation((current) => {
          if (!current) {
            return current;
          }
          const messages = [...current.messages];
          const reply = messages.at(-1);
          if (reply?.sender === 'assistant') {
            const sourcesByNumber = new Map(
              [...(reply.sources ?? []), ...sources].map((source, index) => [
                source.citationNumber ?? index + 1,
                source,
              ])
            );
            const mergedSources = [...sourcesByNumber.values()].sort(
              (a, b) => (a.citationNumber ?? 0) - (b.citationNumber ?? 0)
            );
            messages[messages.length - 1] = {
              ...reply,
              text: toInlineCitationTokens(reply.text, mergedSources),
              sources: mergedSources,
            };
          }
          return { ...current, messages };
        });
        return;
      }

      setLocalConversation((current) => {
        if (!current) {
          return current;
        }
        const messages = [...current.messages];
        const reply = messages.at(-1);
        if (reply?.sender === 'assistant') {
          // A `status` event between two text chunks marks a phase boundary
          // (preamble, then a tool call, then the main answer). The backend
          // streams each phase's tokens raw, with no guaranteed trailing
          // whitespace, so without this the preamble's last word and the
          // answer's first word can run together (see HUL-xxxx).
          const needsSeparator =
            reply.statusBoundaryPending &&
            reply.text !== '' &&
            !/\s$/.test(reply.text);
          messages[messages.length - 1] = {
            ...reply,
            text: reply.text + (needsSeparator ? '\n\n' : '') + event.content,
            statusBoundaryPending: false,
            statuses: undefined,
          };
        }
        return { ...current, messages };
      });
    },
    [finishLastAssistantReply, promoteDraft]
  );

  const sendMessage = useCallback(
    (
      userText: string,
      pageContexts: AiAssistantPageContext[] = [],
      documentContexts: AiAssistantDocumentContext[] = []
    ) => {
      if (!activeChatId || isStreaming) {
        return;
      }

      abortStream();
      setError(null);
      setIsStreaming(true);
      const prompt = buildPrompt(userText, pageContexts, documentContexts);
      const sessionId =
        activeChatId !== null && isDraftChatId(activeChatId)
          ? undefined
          : activeChatId;
      const messages =
        localConversation?.chatId === activeChatId
          ? localConversation.messages
          : mapHistoryMessages(activeChatId, historyQuery.data ?? []);
      const sentAt = Date.now();
      if (!isDraftChatId(activeChatId)) {
        setRecentActivityAt((current) => ({
          ...current,
          [activeChatId]: sentAt,
        }));
      }
      setLocalConversation({
        chatId: activeChatId,
        messages: [
          ...messages,
          {
            id: createMessageId('user'),
            sender: 'user',
            text: userText,
            ...(pageContexts.length > 0 ? { pageContexts } : {}),
            ...(documentContexts.length > 0 ? { documentContexts } : {}),
          },
          {
            id: createMessageId('assistant'),
            sender: 'assistant',
            text: '',
            complete: false,
          },
        ],
      });
      setDraftChat((current) =>
        current?.id === activeChatId
          ? {
              ...current,
              title: userText,
              updatedAt: sentAt,
            }
          : current
      );

      streamRef.current = streamAiAssistantChat({
        prompt,
        sessionId,
        onEvent: handleStreamEvent,
        onError: (streamError) => {
          streamRef.current = null;
          setError(streamError);
          setIsStreaming(false);
          finishLastAssistantReply();
        },
        onComplete: () => {
          streamRef.current = null;
          setIsStreaming(false);
          finishLastAssistantReply();
          void queryClient
            .invalidateQueries({ queryKey: AI_CHAT_SESSIONS_QUERY_KEY })
            .then(() =>
              setDraftChat((current) => {
                const sessions = queryClient.getQueryData<
                  Awaited<ReturnType<typeof fetchAiChatSessions>>
                >(AI_CHAT_SESSIONS_QUERY_KEY);
                return current &&
                  sessions?.some((session) => session.sessionId === current.id)
                  ? null
                  : current;
              })
            );
          void queryClient.invalidateQueries({
            queryKey: ['aiChatSessionHistory'],
          });
        },
      });
    },
    [
      abortStream,
      activeChatId,
      finishLastAssistantReply,
      handleStreamEvent,
      historyQuery.data,
      isStreaming,
      localConversation,
      queryClient,
    ]
  );

  const deleteChat = useCallback(
    async (chatId: string) => {
      setError(null);
      if (isDraftChatId(chatId)) {
        setDraftChat(null);
      } else {
        try {
          await deleteAiChatSession(chatId);
          await queryClient.invalidateQueries({
            queryKey: AI_CHAT_SESSIONS_QUERY_KEY,
          });
        } catch (deleteError) {
          setError(
            deleteError instanceof Error
              ? deleteError
              : new Error(String(deleteError))
          );
          return;
        }
      }

      if (chatId === activeChatId) {
        abortStream();
        setLocalConversation(null);
        const nextChat = chats.find((chat) => chat.id !== chatId);
        setActiveChatId(nextChat?.id ?? null);
      }
    },
    [abortStream, activeChatId, chats, queryClient]
  );

  return {
    activeChatId,
    chats,
    clearError,
    createChat,
    deleteChat,
    error: error ?? sessionsQuery.error ?? historyQuery.error,
    isLoading: sessionsQuery.isLoading || historyQuery.isLoading,
    isStreaming,
    selectChat,
    sendMessage,
  };
}
